import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const TopicClusteringVisualization = ({ category, reviews }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  
  // Predefined mock topics and clusters for demonstration purposes
  const topics = {
    'Product Quality': [
      { name: 'Material Quality', keywords: ['material', 'quality', 'durable', 'solid', 'build', 'construction'] },
      { name: 'Durability', keywords: ['durable', 'lasting', 'long-lasting', 'sturdy', 'robust', 'strong'] },
      { name: 'Appearance', keywords: ['look', 'design', 'appearance', 'color', 'beautiful', 'premium'] },
      { name: 'Performance', keywords: ['performance', 'works', 'function', 'effective', 'efficiency', 'reliable'] }
    ],
    'Customer Service': [
      { name: 'Staff Behavior', keywords: ['staff', 'friendly', 'helpful', 'polite', 'professional', 'courteous'] },
      { name: 'Problem Resolution', keywords: ['problem', 'resolve', 'solution', 'solved', 'fixed', 'addressed'] },
      { name: 'Response Time', keywords: ['response', 'quick', 'timely', 'fast', 'prompt', 'immediately'] },
      { name: 'Knowledge Level', keywords: ['knowledge', 'informed', 'expert', 'knowledgeable', 'trained', 'competent'] }
    ],
    'Delivery & Shipping': [
      { name: 'Delivery Speed', keywords: ['delivery', 'fast', 'quick', 'timely', 'swift', 'prompt'] },
      { name: 'Packaging Quality', keywords: ['packaging', 'packed', 'secure', 'protective', 'undamaged', 'intact'] },
      { name: 'Tracking Updates', keywords: ['tracking', 'update', 'status', 'notification', 'informed', 'communication'] },
      { name: 'Delivery Staff', keywords: ['delivery', 'staff', 'driver', 'courier', 'deliveryman', 'polite'] }
    ],
    'App Experience': [
      { name: 'User Interface', keywords: ['interface', 'ui', 'design', 'layout', 'clean', 'intuitive'] },
      { name: 'App Performance', keywords: ['performance', 'speed', 'fast', 'responsive', 'slow', 'lag'] },
      { name: 'Features', keywords: ['feature', 'functionality', 'options', 'tools', 'capability', 'function'] },
      { name: 'Bugs & Crashes', keywords: ['bug', 'crash', 'error', 'glitch', 'freeze', 'issue'] }
    ],
    'Price & Value': [
      { name: 'Competitiveness', keywords: ['competitive', 'price', 'affordable', 'reasonable', 'fair', 'worth'] },
      { name: 'Discounts', keywords: ['discount', 'offer', 'sale', 'deal', 'promotion', 'coupon'] },
      { name: 'Value Perception', keywords: ['value', 'money', 'worth', 'expensive', 'overpriced', 'premium'] },
      { name: 'Price Transparency', keywords: ['transparent', 'hidden', 'fee', 'charge', 'clear', 'additional'] }
    ]
  };
  
  // Utility function to check if a review contains keywords from a topic
  const reviewContainsTopic = (reviewText, topicKeywords) => {
    const lowercaseText = reviewText.toLowerCase();
    return topicKeywords.some(keyword => lowercaseText.includes(keyword.toLowerCase()));
  };
  
  // Generate clusters based on the selected category and available reviews
  const generateClusters = () => {
    if (!reviews || !reviews.length || !category) return [];
    
    // Get relevant reviews for this category
    const categoryReviews = reviews.filter(review => 
      review.categorySentiments.some(cs => cs.category === category)
    );
    
    if (!categoryReviews.length) return [];
    
    // Get topics for this category
    const categoryTopics = topics[category] || [];
    if (!categoryTopics.length) return [];
    
    // Initialize clusters with one for "Other"
    const clusters = categoryTopics.map(topic => ({
      name: topic.name,
      reviews: [],
      sentiment: { positive: 0, negative: 0, neutral: 0 },
      keywords: topic.keywords
    }));
    
    const otherCluster = {
      name: 'Other',
      reviews: [],
      sentiment: { positive: 0, negative: 0, neutral: 0 },
      keywords: []
    };
    
    // Assign reviews to clusters based on keyword matching
    categoryReviews.forEach(review => {
      const sentiment = review.categorySentiments.find(cs => 
        cs.category === category
      )?.sentiment || 'Neutral';
      
      // Check if review matches any topic
      let foundMatch = false;
      
      for (let i = 0; i < categoryTopics.length; i++) {
        if (reviewContainsTopic(review.reviewText, categoryTopics[i].keywords)) {
          clusters[i].reviews.push({
            ...review,
            topicSentiment: sentiment
          });
          
          // Increment sentiment counter for this cluster
          if (sentiment === 'Positive') {
            clusters[i].sentiment.positive++;
          } else if (sentiment === 'Negative') {
            clusters[i].sentiment.negative++;
          } else {
            clusters[i].sentiment.neutral++;
          }
          
          foundMatch = true;
          break; // Assign to first matching topic
        }
      }
      
      // If no match found, assign to "Other" cluster
      if (!foundMatch) {
        otherCluster.reviews.push({
          ...review,
          topicSentiment: sentiment
        });
        
        // Increment sentiment counter for "Other" cluster
        if (sentiment === 'Positive') {
          otherCluster.sentiment.positive++;
        } else if (sentiment === 'Negative') {
          otherCluster.sentiment.negative++;
        } else {
          otherCluster.sentiment.neutral++;
        }
      }
    });
    
    // Add "Other" cluster if it has reviews
    if (otherCluster.reviews.length > 0) {
      clusters.push(otherCluster);
    }
    
    // Calculate percentages and total for each cluster
    clusters.forEach(cluster => {
      const total = cluster.reviews.length;
      cluster.total = total;
      
      if (total > 0) {
        cluster.positivePercentage = Math.round((cluster.sentiment.positive / total) * 100);
        cluster.negativePercentage = Math.round((cluster.sentiment.negative / total) * 100);
        cluster.neutralPercentage = Math.round((cluster.sentiment.neutral / total) * 100);
      } else {
        cluster.positivePercentage = 0;
        cluster.negativePercentage = 0;
        cluster.neutralPercentage = 0;
      }
    });
    
    // Filter out empty clusters
    return clusters.filter(cluster => cluster.reviews.length > 0);
  };
  
  // Draw the visualization when component mounts or when category/reviews change
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    
    // Clear previous visualization
    d3.select(svgRef.current).selectAll("*").remove();
    
    const clusters = generateClusters();
    if (!clusters.length) return;
    
    // Get container dimensions
    const width = containerRef.current.clientWidth;
    const height = 400;
    
    // Create SVG element
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);
    
    // Create a scale for bubble size based on number of reviews
    const maxReviews = Math.max(...clusters.map(cluster => cluster.reviews.length));
    
    const sizeScale = d3.scaleSqrt()
      .domain([0, maxReviews])
      .range([20, 80]);
    
    // Create force simulation for bubble layout
    const simulation = d3.forceSimulation(clusters)
      .force('x', d3.forceX(width / 2).strength(0.01))
      .force('y', d3.forceY(height / 2).strength(0.01))
      .force('collide', d3.forceCollide().radius(d => sizeScale(d.reviews.length) + 2).strength(0.5));
    
    // Create a color scale for sentiment
    const getClusterColor = (cluster) => {
      const positiveRatio = cluster.positivePercentage / 100;
      const negativeRatio = cluster.negativePercentage / 100;
      
      if (positiveRatio > 0.7) return '#22c55e'; // Mostly positive - green
      if (negativeRatio > 0.7) return '#ef4444'; // Mostly negative - red
      
      if (positiveRatio > negativeRatio) {
        return '#84cc16'; // More positive than negative - lime green
      } else if (negativeRatio > positiveRatio) {
        return '#f97316'; // More negative than positive - orange
      } else {
        return '#94a3b8'; // Balanced - gray
      }
    };
    
    // Create circles for each cluster
    const bubbles = svg.selectAll('.cluster')
      .data(clusters)
      .enter()
      .append('g')
      .attr('class', 'cluster')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelectedCluster(d === selectedCluster ? null : d);
      });
    
    // Add circle to each group
    bubbles.append('circle')
      .attr('r', d => sizeScale(d.reviews.length))
      .attr('fill', d => getClusterColor(d))
      .attr('fill-opacity', 0.7)
      .attr('stroke', d => d === selectedCluster ? '#1e40af' : '#fff')
      .attr('stroke-width', d => d === selectedCluster ? 3 : 1);
    
    // Add text for cluster name
    bubbles.append('text')
      .text(d => d.name)
      .attr('text-anchor', 'middle')
      .attr('dy', '.3em')
      .attr('font-size', d => Math.min(2 * sizeScale(d.reviews.length) / d.name.length, 16))
      .attr('fill', '#fff')
      .attr('pointer-events', 'none');
    
    // Add text for review count
    bubbles.append('text')
      .text(d => d.reviews.length)
      .attr('text-anchor', 'middle')
      .attr('dy', '1.5em')
      .attr('font-size', '10px')
      .attr('fill', '#fff')
      .attr('pointer-events', 'none');
    
    // Update positions on each tick of the simulation
    simulation.on('tick', () => {
      bubbles
        .attr('transform', d => {
          // Keep clusters within bounds
          d.x = Math.max(sizeScale(d.reviews.length), Math.min(width - sizeScale(d.reviews.length), d.x));
          d.y = Math.max(sizeScale(d.reviews.length), Math.min(height - sizeScale(d.reviews.length), d.y));
          
          return `translate(${d.x}, ${d.y})`;
        });
    });
    
    // Clean up
    return () => {
      simulation.stop();
    };
  }, [reviews, category, selectedCluster]);
  
  // Update the visualization when container is resized
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      if (svgRef.current && containerRef.current) {
        d3.select(svgRef.current)
          .attr('width', containerRef.current.clientWidth);
      }
    });
    
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  
  // Get all clusters
  const clusters = generateClusters();
  
  return (
    <div className="">
      <h3 className="text-md font-medium text-gray-900 mb-4">Topic Clusters: {category}</h3>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
          <span className="text-xs text-gray-600">Mostly Positive</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-lime-500 mr-1"></div>
          <span className="text-xs text-gray-600">Mixed (+ leaning)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-orange-500 mr-1"></div>
          <span className="text-xs text-gray-600">Mixed (- leaning)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
          <span className="text-xs text-gray-600">Mostly Negative</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-gray-400 mr-1"></div>
          <span className="text-xs text-gray-600">Balanced</span>
        </div>
      </div>
      
      {/* Visualization */}
      <div 
        ref={containerRef} 
        className="border border-gray-200 rounded-lg bg-gray-50 mb-4" 
        style={{ height: '400px' }}
      >
        <svg ref={svgRef} width="100%" height="400"></svg>
        
        {(!reviews || !reviews.length || !clusters.length) && (
          <div className="flex items-center justify-center h-full text-gray-500">
            No review data available for clustering
          </div>
        )}
      </div>
      
      {/* Selected Cluster Details */}
      {selectedCluster && (
        <div className="mt-4">
          <h4 className="text-md font-medium text-gray-800 mb-2">
            {selectedCluster.name} Cluster
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({selectedCluster.reviews.length} reviews)
            </span>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sentiment Distribution */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Sentiment Distribution</h5>
              
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Positive</span>
                <span className="text-xs font-medium text-green-600">
                  {selectedCluster.positivePercentage}% ({selectedCluster.sentiment.positive})
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: `${selectedCluster.positivePercentage}%` }}></div>
              </div>
              
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Negative</span>
                <span className="text-xs font-medium text-red-600">
                  {selectedCluster.negativePercentage}% ({selectedCluster.sentiment.negative})
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                <div className="bg-red-500 h-2 rounded-full" style={{ width: `${selectedCluster.negativePercentage}%` }}></div>
              </div>
              
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Neutral</span>
                <span className="text-xs font-medium text-gray-600">
                  {selectedCluster.neutralPercentage}% ({selectedCluster.sentiment.neutral})
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-gray-400 h-2 rounded-full" style={{ width: `${selectedCluster.neutralPercentage}%` }}></div>
              </div>
            </div>
            
            {/* Keywords */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Common Keywords</h5>
              
              <div className="flex flex-wrap gap-2">
                {selectedCluster.keywords.map((keyword, idx) => (
                  <span 
                    key={idx}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
                  >
                    {keyword}
                  </span>
                ))}
                
                {selectedCluster.keywords.length === 0 && (
                  <span className="text-xs text-gray-500">No keywords defined for this cluster</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Sample Reviews */}
          <div className="mt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Sample Reviews</h5>
            
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {selectedCluster.reviews.slice(0, 5).map((review, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    review.topicSentiment === 'Positive' ? 'bg-green-50 border-green-200' :
                    review.topicSentiment === 'Negative' ? 'bg-red-50 border-red-200' :
                    'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">{review.userName}</span>
                    <div className="flex">
                      {Array(5).fill(0).map((_, i) => (
                        <svg
                          key={i}
                          className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-1">{review.reviewText}</p>
                  <div className="text-xs text-gray-500 flex justify-between">
                    <span>{new Date(review.date).toLocaleDateString()}</span>
                    <span>via {review.source}</span>
                  </div>
                </div>
              ))}
              
              {selectedCluster.reviews.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-4">
                  No reviews available in this cluster
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicClusteringVisualization;